import { TextField, Grid, MenuItem, Card, CardHeader, CardContent, Checkbox, FormControlLabel, Autocomplete } from "@mui/material"
import { forwardRef, useImperativeHandle } from "react"

import { useFormik } from "formik"
import * as Yup from "yup"
import { useStores } from "stores/root-store"
import { DatePicker } from "@mui/x-date-pickers"
import VirtualizedAutocomplete from "components/common/virtualized-autocomplete"
import { CommonConstant } from "shared/constants/common-constant"
import { GlobalData } from "shared/global-data"

const DGItemProfileInfo = forwardRef(({ detailInfo }, ref) => {
  const { DGItemProfileStore } = useStores()
  const isEditing = Boolean(DGItemProfileStore.detailInfo)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: detailInfo,
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object({
      item: Yup.string().max(50).required("Item is required"),
      class: Yup.string(3).required("Class is required"),
      common_name: Yup.string(250).required("Common Name is required"),
      technical_name: Yup.string(250).required("Technical Name is required"),
      un_number: Yup.number().required("UN Number is required").typeError("UN Number is required"),
      dg_volume: Yup.number().required("DG Volume is required").typeError("DG Volume is required"),
      receptacle_volume: Yup.number().required("Receptacle Volume is required"),
      msds_file_name: Yup.string(250).required("MSDS File Name is required"),
      msds_creation_date: Yup.date().nullable().required("MSDS Creation Date is required").typeError("MSDS Creation Date is required"),
      msds_expiry_date: Yup.date().nullable().required("MSDS Expiry Date is required").typeError("MSDS Expiry Date is required"),
    }),
    onSubmit: async values => {
      //console.log(values)
    },
  })

  useImperativeHandle(ref, () => ({
    async getFormDataIfValid() {
      const val = await formik.validateForm()
      await formik.submitForm()

      if (!val || Object.keys(val).length == 0) return formik.values
      return null
    },
  }))

  const handleDateChanged = (field, newValue) => {
    if (new Date(newValue).getFullYear() > 1900) formik.setFieldValue(field, newValue)
  }

  const handleCheckboxChanged = event => {
    formik.setFieldValue(event.target.name, event.target.checked ? "Y" : "N")
  }

  const packing_groups = ["I", "II", "III"]

  return (
    <form onSubmit={formik.handleSubmit}>
      <Card>
        <CardHeader title="DG Item Profile Information" />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item md={6} xs={12}>
              {isEditing ? (
                <TextField
                  id="item"
                  required
                  label="Item"
                  name="item"
                  value={formik.values.item}
                  placeholder="product / SKU code"
                  fullWidth
                  InputProps={{
                    readOnly: isEditing,
                  }}
                />
              ) : (
                <VirtualizedAutocomplete
                  id="item"
                  readOnly={isEditing}
                  value={formik.values.item}
                  isOptionEqualToValue={(option, value) => option.id === value}
                  onChange={(event, item) => formik.setFieldValue("item", item.id)}
                  options={GlobalData.ProductListSelection}
                  renderInput={paramsInner => (
                    <TextField
                      {...paramsInner}
                      required
                      label="Item"
                      name="item"
                      placeholder="product / SKU code"
                      error={Boolean(formik.touched.item && formik.errors.item)}
                      helperText={formik.touched.item && formik.errors.item}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item md={6} xs={12}>
              <FormControlLabel control={<Checkbox id="active" name="active" checked={formik.values.active == "Y"} onChange={handleCheckboxChanged} />} label="Active" />
            </Grid>

            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                label="UN Number"
                name="un_number"
                id="un_number"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.un_number}
                variant="outlined"
                type="number"
                required
                error={Boolean(formik.touched.un_number && formik.errors.un_number)}
                helperText={formik.touched.un_number && formik.errors.un_number}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                label="DG Volume"
                name="dg_volume"
                id="dg_volume"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.dg_volume}
                variant="outlined"
                type="number"
                required
                error={Boolean(formik.touched.dg_volume && formik.errors.dg_volume)}
                helperText={formik.touched.dg_volume && formik.errors.dg_volume}
              />
            </Grid>

            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                label="Class"
                name="class"
                id="class"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.class}
                variant="outlined"
                inputProps={{ maxLength: 3 }}
                required
                error={Boolean(formik.touched.class && formik.errors.class)}
                helperText={formik.touched.class && formik.errors.class}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField fullWidth label="Packing Group" name="packing_group" id="packing_group" onChange={formik.handleChange} value={formik.values.packing_group} variant="outlined" select>
                {packing_groups.map(option => (
                  <MenuItem id={`packing_group-${option}`} key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                label="Common Name"
                name="common_name"
                id="common_name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.common_name}
                variant="outlined"
                inputProps={{ maxLength: 240 }}
                required
                error={Boolean(formik.touched.common_name && formik.errors.common_name)}
                helperText={formik.touched.common_name && formik.errors.common_name}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                label="Technical Name"
                name="technical_name"
                id="technical_name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.technical_name}
                variant="outlined"
                inputProps={{ maxLength: 240 }}
                required
                error={Boolean(formik.touched.technical_name && formik.errors.technical_name)}
                helperText={formik.touched.technical_name && formik.errors.technical_name}
              />
            </Grid>

            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                label="Receptacle Volume"
                name="receptacle_volume"
                id="receptacle_volume"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.receptacle_volume}
                variant="outlined"
                type="number"
                required
                error={Boolean(formik.touched.receptacle_volume && formik.errors.receptacle_volume)}
                helperText={formik.touched.receptacle_volume && formik.errors.receptacle_volume}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <FormControlLabel control={<Checkbox id="id8000" name="id8000" checked={formik.values.id8000 == "Y"} onChange={handleCheckboxChanged} />} label="ID 8000" />
            </Grid>

            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                name="emergency_contact_name"
                id="emergency_contact_name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.emergency_contact_name}
                variant="outlined"
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                label="Emergency Contact Phone"
                name="emergency_contact_phone"
                id="emergency_contact_phone"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.emergency_contact_phone}
                variant="outlined"
                inputProps={{ maxLength: 30 }}
              />
            </Grid>

            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                label="MSDS File Name"
                name="msds_file_name"
                id="msds_file_name"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.msds_file_name}
                variant="outlined"
                inputProps={{ maxLength: 250 }}
                required
                error={Boolean(formik.touched.msds_file_name && formik.errors.msds_file_name)}
                helperText={formik.touched.msds_file_name && formik.errors.msds_file_name}
              />
            </Grid>
            <Grid item md={3} xs={12}>
              <DatePicker
                format={CommonConstant.DATE_FORMAT}
                label="MSDS Creation Date"
                value={formik.values.msds_creation_date}
                id="msds_creation_date"
                onChange={newValue => handleDateChanged("msds_creation_date", newValue)}
                slotProps={{
                  textField: {
                    name: "msds_creation_date",
                    required: true,
                    fullWidth: true,
                    onBlur: formik.handleBlur,
                    error: Boolean(formik.errors.msds_creation_date),
                    helperText: formik.errors.msds_creation_date,
                  },
                }}
              />
            </Grid>
            <Grid item md={3} xs={12}>
              <DatePicker
                format={CommonConstant.DATE_FORMAT}
                label="MSDS Expiry Date"
                value={formik.values.msds_expiry_date}
                id="msds_expiry_date"
                onChange={newValue => handleDateChanged("msds_expiry_date", newValue)}
                slotProps={{
                  textField: {
                    name: "msds_expiry_date",
                    required: true,
                    fullWidth: true,
                    onBlur: formik.handleBlur,
                    error: Boolean(formik.touched.msds_expiry_date && formik.errors.msds_expiry_date),
                    helperText: formik.touched.msds_expiry_date && formik.errors.msds_expiry_date,
                  },
                }}
              />
            </Grid>

            <Grid item md={6} xs={12}>
              <FormControlLabel
                control={<Checkbox id="marine_pollutant" name="marine_pollutant" checked={formik.values.marine_pollutant == "Y"} onChange={handleCheckboxChanged} />}
                label="Marine Pollutant"
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <FormControlLabel control={<Checkbox id="personal_care" name="personal_care" checked={formik.values.personal_care == "Y"} onChange={handleCheckboxChanged} />} label="Personal Care" />
            </Grid>

            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                label="Battery Weight"
                name="battery_weight"
                id="battery_weight"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.battery_weight}
                variant="outlined"
                type="number"
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <FormControlLabel
                control={<Checkbox id="lithium_battery" name="lithium_battery" checked={formik.values.lithium_battery == "Y"} onChange={handleCheckboxChanged} />}
                label="Lithium Battery"
              />
            </Grid>

            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                label="Subsidary Risk"
                name="subsidary_risk"
                id="subsidary_risk"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.subsidary_risk}
                variant="outlined"
                inputProps={{ maxLength: 30 }}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <TextField
                fullWidth
                label="Flashpoint DEGC"
                name="flashpoint_degc"
                id="flashpoint_degc"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.flashpoint_degc}
                variant="outlined"
                type="number"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </form>
  )
})

export default DGItemProfileInfo
